import { FileText, Check, Eye, Plus, Sparkles } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useTemplates, type Template } from '@/hooks'
import { cn } from '@/lib/utils'

interface TemplateSelectorProps {
  knowledgeBaseId?: string
  onSelect: (template: Template | null) => void
  selectedTemplateId?: string
  className?: string
}

/**
 * 模板选择器组件
 * 显示可用模板列表，支持预览和选择
 */
export function TemplateSelector({
  knowledgeBaseId,
  onSelect,
  selectedTemplateId,
  className,
}: TemplateSelectorProps) {
  const { data: templates, isLoading } = useTemplates(knowledgeBaseId)
  const [previewTemplate, setPreviewTemplate] = useState<Template | null>(null)

  if (isLoading) {
    return <TemplateSelectorSkeleton />
  }

  const globalTemplates = templates?.filter((t) => !t.knowledgeBaseId) ?? []
  const kbTemplates = templates?.filter((t) => t.knowledgeBaseId) ?? []

  return (
    <div className={cn('flex h-full', className)}>
      {/* Template List */}
      <div className="w-64 border-r overflow-auto">
        {/* Blank Document Option */}
        <div className="p-3 border-b">
          <TemplateItem
            template={null}
            isSelected={!selectedTemplateId}
            onSelect={() => onSelect(null)}
            onPreview={() => setPreviewTemplate(null)}
          />
        </div>

        {/* Global Templates */}
        {globalTemplates.length > 0 && (
          <div>
            <div className="px-3 py-2 text-xs font-medium text-muted-foreground bg-muted/30">
              内置模板
            </div>
            <div className="divide-y">
              {globalTemplates.map((template) => (
                <TemplateItem
                  key={template.id}
                  template={template}
                  isSelected={selectedTemplateId === template.id}
                  onSelect={() => onSelect(template)}
                  onPreview={() => setPreviewTemplate(template)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Knowledge Base Templates */}
        {kbTemplates.length > 0 && (
          <div>
            <div className="px-3 py-2 text-xs font-medium text-muted-foreground bg-muted/30">
              知识库模板
            </div>
            <div className="divide-y">
              {kbTemplates.map((template) => (
                <TemplateItem
                  key={template.id}
                  template={template}
                  isSelected={selectedTemplateId === template.id}
                  onSelect={() => onSelect(template)}
                  onPreview={() => setPreviewTemplate(template)}
                />
              ))}
            </div>
          </div>
        )}

        {templates?.length === 0 && (
          <div className="p-4 text-center text-sm text-muted-foreground">
            暂无可用模板
          </div>
        )}
      </div>

      {/* Preview Panel */}
      <div className="flex-1 overflow-auto">
        {previewTemplate ? (
          <TemplatePreview template={previewTemplate} />
        ) : (
          <BlankDocumentPreview />
        )}
      </div>
    </div>
  )
}

// ==================== Sub Components ====================

interface TemplateItemProps {
  template: Template | null
  isSelected: boolean
  onSelect: () => void
  onPreview: () => void
}

function TemplateItem({ template, isSelected, onSelect, onPreview }: TemplateItemProps) {
  return (
    <div
      className={cn(
        'group flex items-center gap-3 p-3 cursor-pointer transition-colors',
        isSelected ? 'bg-primary/10' : 'hover:bg-muted/50'
      )}
      onClick={onSelect}
    >
      <div
        className={cn(
          'flex h-10 w-10 items-center justify-center rounded-lg',
          template?.isBuiltIn ? 'bg-primary/10' : 'bg-muted'
        )}
      >
        {template ? (
          template.isBuiltIn ? (
            <Sparkles className="h-5 w-5 text-primary" />
          ) : (
            <FileText className="h-5 w-5 text-muted-foreground" />
          )
        ) : (
          <Plus className="h-5 w-5 text-muted-foreground" />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm truncate">
            {template?.name ?? '空白文档'}
          </span>
          {isSelected && <Check className="h-4 w-4 text-primary flex-shrink-0" />}
        </div>
        {template?.description && (
          <p className="text-xs text-muted-foreground truncate mt-0.5">
            {template.description}
          </p>
        )}
      </div>

      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 opacity-0 group-hover:opacity-100"
        onClick={(e) => {
          e.stopPropagation()
          onPreview()
        }}
      >
        <Eye className="h-4 w-4" />
      </Button>
    </div>
  )
}

interface TemplatePreviewProps {
  template: Template
}

function TemplatePreview({ template }: TemplatePreviewProps) {
  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="border-b p-4">
        <div className="flex items-center gap-3">
          <div
            className={cn(
              'flex h-12 w-12 items-center justify-center rounded-lg',
              template.isBuiltIn ? 'bg-primary/10' : 'bg-muted'
            )}
          >
            {template.isBuiltIn ? (
              <Sparkles className="h-6 w-6 text-primary" />
            ) : (
              <FileText className="h-6 w-6 text-muted-foreground" />
            )}
          </div>
          <div>
            <h3 className="font-medium">{template.name}</h3>
            {template.description && (
              <p className="text-sm text-muted-foreground">{template.description}</p>
            )}
          </div>
        </div>
      </div>

      {/* Content Preview */}
      <div className="flex-1 overflow-auto p-4">
        <div className="prose prose-sm max-w-none dark:prose-invert">
          <pre className="whitespace-pre-wrap font-mono text-sm bg-muted/30 p-4 rounded-lg">
            {template.content}
          </pre>
        </div>
      </div>
    </div>
  )
}

function BlankDocumentPreview() {
  return (
    <div className="h-full flex flex-col items-center justify-center text-muted-foreground">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
        <Plus className="h-8 w-8" />
      </div>
      <h3 className="font-medium text-foreground">空白文档</h3>
      <p className="text-sm mt-1">从零开始创建文档</p>
    </div>
  )
}

function TemplateSelectorSkeleton() {
  return (
    <div className="flex h-full">
      <div className="w-64 border-r">
        <div className="p-3 border-b">
          <Skeleton className="h-16 w-full" />
        </div>
        <div className="px-3 py-2">
          <Skeleton className="h-4 w-16" />
        </div>
        <div className="divide-y">
          {[1, 2, 3].map((i) => (
            <div key={i} className="p-3">
              <div className="flex items-center gap-3">
                <Skeleton className="h-10 w-10 rounded-lg" />
                <div className="flex-1">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-3 w-32 mt-1" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center">
        <Skeleton className="h-16 w-16 rounded-full" />
      </div>
    </div>
  )
}
